import { useEffect, useState } from 'react';
import type { OddsFormat } from '@hienbet/shared';
import { api, formatChips, formatOddsValue, loginUrl, type BetRecord, type Me, type ParlayRecord } from '../api.js';

/**
 * My bets, as a stack of tickets.
 *
 * A bet is a physical object in every real book — a slip you hold until it
 * wins, loses, or gets torn up. So each bet renders as a ticket with a
 * perforated stub and an ink stamp for its fate, under a record strip that
 * says the only three numbers a bettor actually cares about: the record, the
 * net, and what's still riding.
 */

const STAMP: Record<BetRecord['status'], string> = {
  open: 'LIVE',
  won: 'WON',
  lost: 'LOST',
  void: 'VOID',
};

function when(ts: number): string {
  const d = new Date(ts);
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${date} · ${time}`;
}

export function HistoryPage({ me, oddsFormat }: { me: Me | null; oddsFormat: OddsFormat }) {
  const [bets, setBets] = useState<BetRecord[] | null>(null);
  const [parlays, setParlays] = useState<ParlayRecord[]>([]);
  const [gilded, setGilded] = useState(false);

  useEffect(() => {
    if (!me?.loggedIn) return;
    void api
      .bets()
      .then((data) => {
        setBets(data.bets);
        setParlays(data.parlays ?? []);
        setGilded(data.goldTickets === true);
      })
      .catch(() => setBets([]));
  }, [me?.loggedIn]);

  if (!me?.loggedIn) {
    return (
      <div className="page">
        <header className="page-head">
          <h1>My bets</h1>
          <p>Log in to see your bets and balance.</p>
        </header>
        <a className="place as-link inline" href={loginUrl()}>
          Log in with Twitch
        </a>
      </div>
    );
  }

  // Micro bets are frequent and small; settled ones collapse into their own
  // compact list so the real tickets stay readable. Open ones stay up top —
  // they are live action.
  const open = bets?.filter((b) => b.status === 'open') ?? [];
  const done = bets?.filter((b) => b.status !== 'open' && !b.micro) ?? [];
  const microDone = bets?.filter((b) => b.status !== 'open' && b.micro) ?? [];
  const settled = bets?.filter((b) => b.status !== 'open') ?? [];
  const wins = settled.filter((b) => b.status === 'won').length;
  const losses = settled.filter((b) => b.status === 'lost').length;
  // Net over settled bets only; voids returned the stake, so they are zero.
  const net = settled.reduce((sum, b) => sum + b.payout - (b.status === 'void' ? b.payout : b.stake), 0);
  const riding = open.reduce((sum, b) => sum + b.stake, 0);

  return (
    <div className="page">
      <header className="page-head">
        <h1>My bets</h1>
        <p>Every ticket keeps the price it was struck at, whatever the line did after.</p>
      </header>

      {bets === null && <p className="note">Loading…</p>}
      {bets?.length === 0 && (
        <p className="note">No bets yet. The board is where that changes.</p>
      )}

      {bets && bets.length > 0 && (
        <>
          <div className="record-strip">
            <div className="record-cell">
              <span className="label">Record</span>
              <span className="record-value num">
                {wins}–{losses}
              </span>
            </div>
            <div className="record-cell">
              <span className="label">Net</span>
              <span className={`record-value num ${net > 0 ? 'up' : net < 0 ? 'down' : ''}`}>
                {net > 0 ? '+' : ''}
                {formatChips(net)}
              </span>
            </div>
            <div className="record-cell">
              <span className="label">Riding</span>
              <span className="record-value num">{riding > 0 ? formatChips(riding) : '—'}</span>
            </div>
          </div>

          {parlays.length > 0 && (
            <>
              <h2 className="section">Parlays</h2>
              <ul className={`tickets ${gilded ? 'gilded' : ''}`}>
                {parlays.map((parlay) => (
                  <ParlayTicket key={parlay.id} parlay={parlay} oddsFormat={oddsFormat} />
                ))}
              </ul>
            </>
          )}

          {open.length > 0 && (
            <>
              <h2 className="section">Open</h2>
              <ul className={`tickets ${gilded ? 'gilded' : ''}`}>
                {open.map((bet) => (
                  <Ticket key={bet.id} bet={bet} oddsFormat={oddsFormat} />
                ))}
              </ul>
            </>
          )}

          {done.length > 0 && (
            <>
              <h2 className="section">Settled</h2>
              <ul className={`tickets ${gilded ? 'gilded' : ''}`}>
                {done.map((bet) => (
                  <Ticket key={bet.id} bet={bet} oddsFormat={oddsFormat} />
                ))}
              </ul>
            </>
          )}

          {microDone.length > 0 && <MicroHistory bets={microDone} />}
        </>
      )}
    </div>
  );
}

/** Settled in-play bets, one line each — volume without the clutter. */
function MicroHistory({ bets }: { bets: BetRecord[] }) {
  const net = bets.reduce((sum, b) => sum + b.payout - (b.status === 'void' ? b.payout : b.stake), 0);

  return (
    <>
      <h2 className="section">
        In-play
        <span className={`micro-net num ${net > 0 ? 'up' : net < 0 ? 'down' : ''}`}>
          {net > 0 ? '+' : ''}
          {formatChips(net)}
        </span>
      </h2>
      <ul className="micro-history">
        {bets.map((bet) => (
          <li key={bet.id} className={bet.status}>
            <span className={`micro-dot ${bet.status}`} />
            <span className="micro-what">
              {bet.market || bet.selection} — {bet.selection}
            </span>
            <span className="num micro-amount">
              {bet.status === 'won' && <span className="up">+{formatChips(bet.payout - bet.stake)}</span>}
              {bet.status === 'lost' && <span className="down">−{formatChips(bet.stake)}</span>}
              {bet.status === 'void' && <span>refunded</span>}
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

/** A parlay renders as one ticket with its legs stacked inside. */
function ParlayTicket({ parlay, oddsFormat }: { parlay: ParlayRecord; oddsFormat: OddsFormat }) {
  const toWin = Math.floor((parlay.stake * parlay.combinedMilli) / 1000) - parlay.stake;
  const status = parlay.status as BetRecord['status'];

  return (
    <li className={`ticket ${status}`}>
      <div className="ticket-main">
        <div className="ticket-selection">Parlay ×{parlay.legs.length}</div>
        <ul className="parlay-legs">
          {parlay.legs.map((leg, i) => (
            <li key={i} className={leg.result}>
              <span className={`leg-dot ${leg.result}`} />
              <span className="leg-what">
                {leg.marketLabel} — {leg.selectionLabel}
              </span>
              <span className="num leg-odds">{formatOddsValue(leg.milliOdds, oddsFormat)}</span>
            </li>
          ))}
        </ul>
        <div className="ticket-meta">
          <span className="num">{formatOddsValue(parlay.combinedMilli, oddsFormat)} combined</span>
          <span>·</span>
          <span>{when(parlay.placedAt)}</span>
        </div>
      </div>

      <div className="ticket-stub">
        <span className={`ticket-stamp ${status}`}>{STAMP[status] ?? status.toUpperCase()}</span>
        <div className="ticket-money">
          <span className="label">Stake</span>
          <span className="num ticket-stake">{formatChips(parlay.stake)}</span>
        </div>
        <div className="ticket-money">
          {status === 'open' && (
            <>
              <span className="label">To win</span>
              <span className="num ticket-return up">+{formatChips(toWin)}</span>
            </>
          )}
          {status === 'won' && (
            <>
              <span className="label">Profit</span>
              <span className="num ticket-return up">+{formatChips(parlay.payout - parlay.stake)}</span>
            </>
          )}
          {status === 'lost' && (
            <>
              <span className="label">Lost</span>
              <span className="num ticket-return down">−{formatChips(parlay.stake)}</span>
            </>
          )}
          {status === 'void' && (
            <>
              <span className="label">Returned</span>
              <span className="num ticket-return">{formatChips(parlay.payout)}</span>
            </>
          )}
        </div>
      </div>
    </li>
  );
}

function Ticket({ bet, oddsFormat }: { bet: BetRecord; oddsFormat: OddsFormat }) {
  const toWin = Math.floor((bet.stake * bet.milliOdds) / 1000) - bet.stake;

  return (
    <li className={`ticket ${bet.status}`}>
      <div className="ticket-main">
        <div className="ticket-selection">{bet.selection}</div>
        <div className="ticket-market">{bet.market || '—'}</div>
        <div className="ticket-meta">
          <span className="num">{formatOddsValue(bet.milliOdds, oddsFormat)}</span>
          <span>·</span>
          <span>{when(bet.placedAt)}</span>
        </div>
      </div>

      <div className="ticket-stub">
        <span className={`ticket-stamp ${bet.status}`}>{STAMP[bet.status]}</span>
        <div className="ticket-money">
          <span className="label">Stake</span>
          <span className="num ticket-stake">{formatChips(bet.stake)}</span>
        </div>
        <div className="ticket-money">
          {bet.status === 'open' && (
            <>
              <span className="label">To win</span>
              <span className="num ticket-return up">+{formatChips(toWin)}</span>
            </>
          )}
          {bet.status === 'won' && (
            <>
              <span className="label">Profit</span>
              <span className="num ticket-return up">+{formatChips(bet.payout - bet.stake)}</span>
            </>
          )}
          {bet.status === 'lost' && (
            <>
              <span className="label">Lost</span>
              <span className="num ticket-return down">−{formatChips(bet.stake)}</span>
            </>
          )}
          {bet.status === 'void' && (
            <>
              <span className="label">Returned</span>
              <span className="num ticket-return">{formatChips(bet.payout)}</span>
            </>
          )}
        </div>
      </div>
    </li>
  );
}
